import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useRouter } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AppShell, Badge, Button } from '@projexn/ui';
import { authClient } from '../lib/auth-client';
import { SESSION_QUERY_KEY } from '../lib/session';
import { listPending, startQueueReplay } from '../lib/mutation-queue';
import { startRealtime } from '../lib/realtime';

export function AppLayout() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const { data: session } = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => authClient.getSession(),
    staleTime: 60_000,
  });
  const user = session?.data?.user;

  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pending, setPending] = useState(0);

  // Realtime WS only makes sense once we're authed on a tenant host — starting
  // it on /login (no tenant context) just spams EPIPE errors in the Vite proxy
  // while wrangler is still booting.
  useEffect(() => {
    const stopRt = startRealtime(queryClient);
    const stopQueue = startQueueReplay(queryClient, setPending);
    return () => {
      stopRt();
      stopQueue();
    };
  }, [queryClient]);

  useEffect(() => {
    const refresh = async () => setPending((await listPending()).length);
    refresh();
    const onOnline = () => { setOnline(true); refresh(); };
    const onOffline = () => setOnline(false);
    const interval = setInterval(refresh, 5000);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      clearInterval(interval);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const tenant = window.location.host.split('.')[0];

  const nav = [
    { label: 'Pipeline', href: '/pipeline' },
    { label: 'Workload', href: '/workload' },
    { label: 'Takeoffs', href: '/takeoffs' },
    { label: 'Quotes', href: '/quotes' },
    { label: 'RFIs', href: '/rfis' },
    { label: 'Visits', href: '/site-visits' },
    { label: 'Inventory', href: '/inventory' },
    { label: 'Clients', href: '/clients' },
    { label: 'Admin', href: '/admin' },
  ].map((n) => ({ ...n, active: pathname.startsWith(n.href) }));

  return (
    <AppShell
      brand={<Link to="/pipeline" className="hover:underline">Tender · {tenant}</Link>}
      nav={nav}
      renderNavLink={(item, children) => (
        <Link to={item.href} className="block">
          {children}
        </Link>
      )}
      headerRight={
        <>
          {!online && <Badge tone="warning">Offline</Badge>}
          {pending > 0 && (
            <Badge tone="info">{pending} pending sync</Badge>
          )}
          <span className="text-sm text-zinc-500">{user?.email}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              await authClient.signOut();
              router.invalidate();
              router.navigate({ to: '/login' });
            }}
          >
            Sign out
          </Button>
        </>
      }
    >
      <Outlet />
    </AppShell>
  );
}
